import { Modal, Button, Stack, Group, Text, Box, useMantineColorScheme, LoadingOverlay } from "@mantine/core";
import { ReportVariable } from "@bi-studio/shared/lib";
import { VariableInput } from "../VariableInput";
import { PREDEFINED_VARIABLES } from "@bi-studio/shared/lib";

interface PreviewVariablesModalProps {
    opened: boolean;
    onClose: () => void;
    variables: ReportVariable[];
    values: Record<string, string>;
    onValuesChange: (values: Record<string, string>) => void;
    onConfirm: () => void;
    dataSourceId: string;
    loading?: boolean;
}

export function PreviewVariablesModal({
    opened,
    onClose,
    variables,
    values,
    onValuesChange,
    onConfirm,
    dataSourceId,
    loading,
}: PreviewVariablesModalProps) {
    const { colorScheme } = useMantineColorScheme();
    // Filter out predefined variables that don't need user input
    const userVariables = variables.filter(v => !PREDEFINED_VARIABLES.includes(v.name));

    if (userVariables.length === 0) return null;

    const handleValueChange = (name: string, value: string) => {
        onValuesChange({
            ...values,
            [name]: value,
        });
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={<Text fw={600}>Preview Query Variables</Text>}
            size="md"
            centered
            overlayProps={{ backgroundOpacity: 0.1, blur: 2 }}
        >
            <LoadingOverlay visible={loading} overlayProps={{ backgroundOpacity: 0.1, blur: 2 }} />
            <Stack gap="md">
                <Text size="sm" c="dimmed">
                    This query contains variables. Please provide values for the following variables to preview the result.
                </Text>

                <Box
                    p="md"
                    style={{
                        border: '1px solid var(--modal-border)',
                        borderRadius: 'var(--mantine-radius-md)',
                        background: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.01)' : 'rgba(255, 255, 255, 0.5)',
                        backdropFilter: colorScheme === 'light' ? 'blur(10px)' : undefined
                    }}
                >
                    <Stack gap="md">
                        {userVariables.map((variable) => (
                            <VariableInput
                                key={variable.name}
                                name={variable.name}
                                config={variable}
                                value={values[variable.name] || ""}
                                onChange={(val: string) => handleValueChange(variable.name, val)}
                                dataSourceId={dataSourceId}
                            />
                        ))}
                    </Stack>
                </Box>

                <Group justify="flex-end" pt="md">
                    <Button 
                        variant={colorScheme === 'dark' ? 'light' : 'subtle'} 
                        color="gray" 
                        onClick={onClose} 
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button 
                        color={colorScheme === 'dark' ? 'gray.8' : 'brand'} 
                        onClick={onConfirm} 
                        loading={loading}
                        variant="filled"
                    >
                        Run Preview
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
}
